import { Dayjs } from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '@/shared/components/Modal/Modal'
import Button from '@/shared/components/Button/Button'
import CheckBox from '@/shared/components/CheckBox/CheckBox'
import Alert from '@/shared/components/Alert/Alert'
import DatePicker, {
  DateRangeValue
} from '@/shared/components/DatePicker/DatePicker'
import { useEmploymentProfiles } from '@/features/employee-management/hooks/useEmploymentProfiles'
import { Shift } from '../../types/schedule.model'
import { DateRange, buildPayrollRows } from '../../utils/payroll'
import { downloadCsv, payrollCsv, rosterCsv } from '../../utils/export'
import { useScheduleWeeksQuery } from '../../hooks/useScheduleQuery'
import { useScheduleStore } from '../../store/useScheduleStore'
import {
  dayjs,
  startOfWeek,
  toDateKey,
  weekStartsCovering
} from '../../utils/schedule'
import styles from './ScheduleExportModal.module.scss'

interface ScheduleExportModalProps {
  isOpen: boolean
  onClose: () => void
  /** The period on screen, the range opens on its month */
  month: Dayjs
  employees: Array<{ userId: string; name: string }>
  shifts: Shift[]
}

type Preset = 'day' | 'week' | 'month'

/** A schedule is planned forward, so the picker must allow future dates. */
const MAX_EXPORT_DATE = dayjs().add(2, 'year')

function ScheduleExportModal(props: ScheduleExportModalProps) {
  const { isOpen, onClose, month, employees, shifts } = props
  const { t } = useTranslation()

  const { profilesByUserId } = useEmploymentProfiles()
  // The night column in the file is the one on screen.
  const nightWindow = useScheduleStore(state => state.nightWindow)

  const [range, setRange] = useState<DateRangeValue>([
    month.startOf('month'),
    month.endOf('month')
  ])
  const [payrollChecked, setPayrollChecked] = useState(true)
  const [rosterChecked, setRosterChecked] = useState(false)

  // Reopening on a different month should offer that month, not the last one
  useEffect(() => {
    if (isOpen) setRange([month.startOf('month'), month.endOf('month')])
  }, [isOpen, month])

  const applyPreset = (preset: Preset) => {
    if (preset === 'day') {
      setRange([month.startOf('day'), month.endOf('day')])
      return
    }
    if (preset === 'week') {
      const monday = startOfWeek(month)
      setRange([monday, monday.add(6, 'day')])
      return
    }
    setRange([month.startOf('month'), month.endOf('month')])
  }

  const [from, to] = range

  // The picked range can reach outside the month the page has loaded, and the
  // store only holds weeks that have actually been fetched, exporting without
  // these would quietly write a CSV that is missing shifts.
  const exportWeeks = useMemo(
    () => (isOpen && from && to ? weekStartsCovering(from, to) : []),
    [isOpen, from, to]
  )

  const {
    isLoading: rangeLoading,
    failedWeeks,
    isForbidden
  } = useScheduleWeeksQuery(exportWeeks)

  // Exporting is blocked rather than warned about: a CSV that is quietly short
  // a week is worse than no CSV, because nothing downstream can tell.
  const canExport =
    !!from &&
    !!to &&
    (payrollChecked || rosterChecked) &&
    !rangeLoading &&
    !failedWeeks.length

  const handleExport = () => {
    if (!from || !to) return

    const exportRange: DateRange = { from, to }
    const label = `${toDateKey(from)}_${toDateKey(to)}`

    if (payrollChecked) {
      const rows = buildPayrollRows(
        employees,
        shifts,
        profilesByUserId,
        exportRange,
        undefined,
        nightWindow
      )
      downloadCsv(`grafik-wyplaty-${label}.csv`, payrollCsv(rows, t))
    }

    if (rosterChecked) {
      downloadCsv(
        `grafik-zmiany-${label}.csv`,
        rosterCsv(employees, shifts, exportRange, t)
      )
    }

    onClose()
  }

  return (
    <Modal
      title={t('SCHEDULE.EXPORT_MODAL.TITLE')}
      isOpen={isOpen}
      size="small"
      onClose={onClose}
    >
      <div className={styles.container}>
        <p className={`body-medium ${styles.description}`}>
          {t('SCHEDULE.EXPORT_MODAL.DESCRIPTION')}
        </p>

        <div className={styles.presetRow}>
          <Button
            label={t('SCHEDULE.EXPORT_MODAL.PRESET_DAY')}
            variant="outlined"
            color="primary"
            onClick={() => applyPreset('day')}
          />
          <Button
            label={t('SCHEDULE.EXPORT_MODAL.PRESET_WEEK')}
            variant="outlined"
            color="primary"
            onClick={() => applyPreset('week')}
          />
          <Button
            label={t('SCHEDULE.EXPORT_MODAL.PRESET_MONTH')}
            variant="outlined"
            color="primary"
            onClick={() => applyPreset('month')}
          />
        </div>

        <DatePicker
          label={t('COMMON.DATE_RANGE')}
          value={range}
          onChange={setRange}
          // The shared picker caps at today by default, which would make next
          // month's roster impossible to export.
          maxDate={MAX_EXPORT_DATE}
        />

        {!!failedWeeks.length && (
          <Alert
            severity="error"
            message={t(
              isForbidden
                ? 'SCHEDULE.WEEKS_FORBIDDEN'
                : 'SCHEDULE.EXPORT_MODAL.WEEKS_FAILED'
            )}
            onClose={() => undefined}
          />
        )}

        <div className={styles.checkboxContainer}>
          <CheckBox
            label={t('SCHEDULE.EXPORT_MODAL.PAYROLL')}
            value={payrollChecked}
            onChange={() => setPayrollChecked(!payrollChecked)}
          />
          <CheckBox
            label={t('SCHEDULE.EXPORT_MODAL.ROSTER')}
            value={rosterChecked}
            onChange={() => setRosterChecked(!rosterChecked)}
          />
        </div>

        <div className={styles.buttonContainer}>
          <Button
            color="secondary"
            label={t('COMMON.CANCEL')}
            variant="contained"
            onClick={onClose}
          />
          <Button
            color="primary"
            label={t('SCHEDULE.EXPORT_MODAL.EXPORT')}
            variant="contained"
            onClick={handleExport}
            disabled={!canExport}
          />
        </div>
      </div>
    </Modal>
  )
}

export default ScheduleExportModal
